import { Warning } from "@phosphor-icons/react";
import clsx from "clsx";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { HTTPError } from "common/helpers/HTTP";

interface PreviewContentProps {
  message: string;
  subject: string;
  isLoading: boolean;
  onRefetch: () => void;
  error: Error;
}

const defaultMessage = "Your preview will be shown here...";

export default function PreviewContent({
  message,
  subject,
  isLoading,
  onRefetch,
  error,
}: PreviewContentProps) {
  let subjectContent = <span className="text-sm">{subject}</span>;
  let messageContent = (
    <p
      className={clsx(
        "whitespace-pre-wrap break-words text-sm",
        !message && "text-black-500",
      )}
    >
      {message || defaultMessage}
    </p>
  );

  if (error) {
    const errorMessage =
      error instanceof HTTPError && error.data === "VariablesMissingError"
        ? "Cannot show preview, or send message: Missing variable. Make sure all variables have fallbacks/prompts"
        : "Error loading preview";
    messageContent = (
      <div className="flex items-center gap-1">
        <Warning size={16} weight="fill" className="fill-red-500" />
        <p className="text-xs text-red-500">{errorMessage}</p>
      </div>
    );
  } else if (isLoading) {
    messageContent = (
      <div className="flex flex-col gap-1">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-8/12" />
      </div>
    );
    subjectContent = <Skeleton className="h-5 w-6/12" />;
  }

  return (
    <>
      {subject && (
        <div className="mx-2 flex flex-col border-b border-b-black-200 px-1 py-2">
          <span className="text-button-xxs text-black-500">Subject</span>
          {subjectContent}
        </div>
      )}
      <div className="flex-1 flex-col overflow-y-auto p-3 scrollbar-thin">
        {messageContent}
      </div>
      {!isLoading && onRefetch && (
        <Button
          onClick={onRefetch}
          variant="outline-black"
          className="absolute inset-x-0 bottom-3 mx-auto w-fit self-center"
          size="sm"
        >
          {error ? "Try again" : "Update changes"}
        </Button>
      )}
    </>
  );
}
